// CalificarTiendanubeModal — "Calificanos en Tiendanube y recibí 10 imágenes gratis"
// (ADR-2026-10-12-premio-por-calificar-en-tiendanube).
//
// Primero califica acá, de 1 a 5 estrellas
// (ADR-2026-10-27-calificar-con-estrellas-antes-de-tiendanube):
//   - 1 a 3: se le agradece y se le acreditan las imágenes en el acto. No se lo manda a
//     dejar esa nota en público.
//   - 4 o 5: el recorrido de siempre en Tiendanube.
// Las estrellas van a PostHog (`calificacion_atlas_estrellas`); el backend no las ve.
//
// En Tiendanube no hay forma de saber si la persona calificó: Tiendanube no avisa. Lo que
// se premia es el gesto, así que el recorrido es:
//   1. "Ir a calificar" abre la página de la app en Tiendanube en otra pestaña.
//   2. Recién entonces aparece "Hecho", que reclama el premio al backend (una vez por
//      empresa; el candado es del servidor).
//   3. Confirmación con lo que el servidor dijo que acreditó.
// Pedir que abra la página antes de "Hecho" es lo único que se puede exigir, y alcanza
// para que el premio no salga con un solo click sin haber visto la página.

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Check, ExternalLink, Loader2, Star, X } from "lucide-react";
import {
  PREMIO_CALIFICAR_TIENDANUBE,
  URL_CALIFICAR_TIENDANUBE,
} from "../lib/calificacionTiendanube";
import { reclamarPremioCalificacion } from "../lib/calificacionTiendanubeReward";
import { trackEvent } from "../lib/posthog";

/** Desde cuántas estrellas se le pide que deje la calificación en Tiendanube. */
export const ESTRELLAS_PARA_TIENDANUBE = 4;

const BOTON_PRIMARIO =
  "inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-6 py-3 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-60";

export const CalificarTiendanubeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  /** El premio quedó resuelto (acreditado ahora o ya cobrado antes): el cartel se va. */
  onReclamado: () => void;
}> = ({ open, onClose, onReclamado }) => {
  const [estrellas, setEstrellas] = useState(0);
  const [estrellasHover, setEstrellasHover] = useState(0);
  /** Ya eligió sus estrellas y fueron 4 o 5: se muestra el paso de Tiendanube. */
  const [enTiendanube, setEnTiendanube] = useState(false);
  const [abrioLaPagina, setAbrioLaPagina] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acreditadas, setAcreditadas] = useState<number | null>(null);
  const [yaCobrado, setYaCobrado] = useState(false);

  if (!open) return null;

  const cerrar = () => {
    if (!enviando) onClose();
  };

  const irACalificar = () => {
    window.open(URL_CALIFICAR_TIENDANUBE, "_blank", "noopener,noreferrer");
    setAbrioLaPagina(true);
  };

  const hecho = async () => {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    const r = await reclamarPremioCalificacion();
    setEnviando(false);
    if (r.status === "error") {
      setError(r.message);
      return;
    }
    if (r.status === "already_granted") setYaCobrado(true);
    else setAcreditadas(r.imagesCredited);
    onReclamado();
  };

  const continuar = () => {
    if (!estrellas || enviando) return;
    // Un reintento (el reclamo falló) no vuelve a contar la misma calificación.
    if (!error) trackEvent("calificacion_atlas_estrellas", { estrellas });
    if (estrellas >= ESTRELLAS_PARA_TIENDANUBE) setEnTiendanube(true);
    else void hecho();
  };

  const terminado = acreditadas !== null || yaCobrado;
  const estrellasVisibles = estrellasHover || estrellas;

  return createPortal(
    // Portal a `document.body`: el panel de contenido del desktop es `position: fixed`
    // y un overlay montado adentro queda por debajo de la sidebar.
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={cerrar}
    >
      <div
        className="w-full rounded-t-[14px] bg-white shadow-2xl sm:max-w-md sm:rounded-[14px]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Calificanos en Tiendanube"
      >
        <div className="flex items-center justify-end px-3 pt-3">
          <button
            type="button"
            onClick={cerrar}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 transition-colors hover:bg-black/[0.1]"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {terminado ? (
          <div className="animate-paso-adelante px-6 pb-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary">
              <Check className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-[20px] font-semibold leading-tight text-brand-black/90">
              {enTiendanube ? "¡Gracias por calificarnos!" : "¡Gracias por tu valoración!"}
            </h2>
            <p className="mt-1.5 text-[15px] leading-snug text-[#3C3C43]/60">
              {yaCobrado
                ? "Este premio ya estaba acreditado en tu cuenta."
                : `Te sumamos ${acreditadas} imágenes gratis a tu cuenta.`}
            </p>
            <button type="button" onClick={onClose} className={`mt-6 ${BOTON_PRIMARIO}`}>
              Listo
            </button>
          </div>
        ) : !enTiendanube ? (
          <div className="px-6 pb-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary">
              <Star className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-[20px] font-semibold leading-tight text-brand-black/90">
              ¿Qué te parece Atlas?
            </h2>
            <p className="mt-1.5 text-[15px] leading-snug text-[#3C3C43]/60">
              Calificá tu experiencia y recibí {PREMIO_CALIFICAR_TIENDANUBE} imágenes gratis.
            </p>

            <div
              className="mt-5 flex justify-center gap-1"
              role="radiogroup"
              aria-label="Calificación de 1 a 5 estrellas"
              onMouseLeave={() => setEstrellasHover(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={estrellas === n}
                  aria-label={`${n} ${n === 1 ? "estrella" : "estrellas"}`}
                  disabled={enviando}
                  onClick={() => setEstrellas(n)}
                  onMouseEnter={() => setEstrellasHover(n)}
                  className="rounded-md p-1.5 transition-transform duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary motion-safe:active:scale-[0.96]"
                >
                  <Star
                    className={`h-9 w-9 transition-colors duration-150 ${
                      n <= estrellasVisibles
                        ? "fill-amber-400 text-amber-400"
                        : "fill-transparent text-brand-black/20"
                    }`}
                  />
                </button>
              ))}
            </div>

            {error && (
              <p className="mt-4 rounded-xl bg-red-50 px-4 py-2 text-[14px] text-red-700">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={continuar}
              disabled={!estrellas || enviando}
              className={`mt-6 ${BOTON_PRIMARIO}`}
            >
              {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
              {error ? "Reintentar" : "Continuar"}
            </button>
          </div>
        ) : (
          <div className="animate-paso-adelante px-6 pb-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary">
              <Star className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-[20px] font-semibold leading-tight text-brand-black/90">
              ¡Gracias! Dejá tu calificación en Tiendanube y recibí{" "}
              {PREMIO_CALIFICAR_TIENDANUBE} imágenes gratis
            </h2>
            <p className="mt-1.5 text-[15px] leading-snug text-[#3C3C43]/60">
              Contanos qué te parece Atlas en la tienda de aplicaciones de Tiendanube. Tu
              opinión ayuda a otras marcas a conocernos.
            </p>

            <ol className="mt-5 flex flex-col gap-2 text-[14px] text-brand-black/80">
              <li className="flex items-start gap-2">
                <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-[12px] font-semibold text-brand-primary">
                  1
                </span>
                Abrí la página de Atlas en Tiendanube y dejá tu calificación.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-[12px] font-semibold text-brand-primary">
                  2
                </span>
                Volvé acá y tocá “Hecho” para recibir tus imágenes.
              </li>
            </ol>

            {error && (
              <p className="mt-4 rounded-xl bg-red-50 px-4 py-2 text-[14px] text-red-700">
                {error}
              </p>
            )}

            <div className="mt-6 flex flex-col gap-2">
              {abrioLaPagina ? (
                <>
                  <button
                    type="button"
                    onClick={hecho}
                    disabled={enviando}
                    className={BOTON_PRIMARIO}
                  >
                    {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
                    Hecho
                  </button>
                  <button
                    type="button"
                    onClick={irACalificar}
                    disabled={enviando}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-medium text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.085)]"
                  >
                    Volver a abrir la página
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <button type="button" onClick={irACalificar} className={BOTON_PRIMARIO}>
                  Ir a calificar
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CalificarTiendanubeModal;
